import React, { useEffect, useRef, useState, useMemo } from 'react';
import SliderItemWrapper from './SliderItemWrapper';
import { useSelector } from 'react-redux';
function InfiniteSlider({
  children,
  activeIndexCallback,
  activeAnimationCoefficient,
  pausedAnimationCoefficient,
  styles,
  classNames,
  forceReset,
  sliderId,
  isCentered,
  activeBigger,
  indicator,
  activeIndicatorColor,
  inactiveIndicator,
}) {
  const containerRef = useRef(null);
  const [sliderScrollWidth, setSliderScrollWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState(isCentered ? 2 : 1);
  const [keyframes, setKeyframes] = useState(false);
  const [reset, setReset] = useState(false);
  const windowWidth = useSelector(state => state.eventListenerSlice?.windowSize)?.width;
  const screenShotMode = useSelector(state => state.systemSlice.screenShotMode);

  useEffect(() => {
    setReset(state => !state);
  }, [forceReset, children?.length, windowWidth]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setReset(state => !state);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('resize', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('resize', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    activeIndexCallback && activeIndexCallback(activeIndex);
  }, [activeIndex]);

  useEffect(() => {
    if (containerRef.current) {
      const ssw = containerRef.current?.scrollWidth;
      setSliderScrollWidth(ssw / 2);
      setActiveIndex(isCentered ? 2 : 1);
    }
  }, [reset]);

  useEffect(() => {
    if (!screenShotMode) {
      // Set your state at regular intervals
      const intervalId = setInterval(() => {
        setActiveIndex(state => (state + 1) % children?.length);
      }, (activeAnimationCoefficient + pausedAnimationCoefficient) * 1000);

      return () => clearInterval(intervalId);
    }
  }, [sliderScrollWidth, reset, screenShotMode]);

  useEffect(() => {
    if (!screenShotMode) {
      function createDynamicKeyframes(selector, pauseDuration, animationDuration, itemLength) {
        const percentStep = 100 / itemLength;
        const activePercent = (animationDuration / (pauseDuration + animationDuration)) * percentStep;
        const pausedPercent = (pauseDuration / (pauseDuration + animationDuration)) * percentStep;
        const containerWidth = containerRef?.current?.clientWidth;
        const sliderItemWidth = (1 / itemLength) * sliderScrollWidth;
        const percentItemWidth = sliderItemWidth / containerWidth;
        const gapWidth = containerWidth * (1 - percentItemWidth);
        const keyframes = Array.from(
          { length: itemLength + 1 },
          (_, index) =>
            ` ${index > 0 ? activePercent + (activePercent + pausedPercent) * (index - 1) : 0}% {
                    transform: translateX(-${
                      index * sliderItemWidth - (isCentered ? -sliderItemWidth + gapWidth / 2 : 0)
                    }px);
                    }
                    ${index * (activePercent + pausedPercent)}% {
                    transform: translateX(-${
                      index * sliderItemWidth - (isCentered ? -sliderItemWidth + gapWidth / 2 : 0)
                    }px);
                    }
                `,
        ).join('\n');
        return `@keyframes ${selector} {${keyframes}}`;
      }

      const newKeyframes = createDynamicKeyframes(
        `animateContainer_${sliderId}`,
        pausedAnimationCoefficient,
        activeAnimationCoefficient,
        children?.length,
      );

      setKeyframes('');

      setTimeout(() => {
        setKeyframes(newKeyframes);
      }, 10);
    } else {
      setKeyframes('');
    }
  }, [sliderScrollWidth, reset, sliderId, screenShotMode]);
  const modifiedChildren = React.Children.map(children, (child, index) => {
    // Clone each child with additional props
    return (
      <SliderItemWrapper
        key={index}
        sliderScrollWidth={sliderScrollWidth}
        reset={reset}
        sliderId={sliderId}
        classNames={classNames?.SliderItemWrapper}
        style={{
          ...styles?.SliderItemWrapper,
          animationName: `animateContainer_${sliderId}`,
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          animationDuration: `${(activeAnimationCoefficient + pausedAnimationCoefficient) * children.length}s`,
        }}
      >
        {activeBigger ? (
          <div
            style={{
              transform: activeIndex === index ? 'scale(1)' : 'scale(0.9)',
              transitionDuration: `${activeAnimationCoefficient / 2}s`,
              transitionDelay: `${activeAnimationCoefficient / 2}s`,
              width: '100%',
              height: '100%',
            }}
          >
            {child}
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            {child}
          </div>
        )}
      </SliderItemWrapper>
    );
  });

  const copyModifiedChildren = useMemo(() => {
    return modifiedChildren.map(element => React.cloneElement(element));
  }, [modifiedChildren]);

  return (
    <>
      <div className="flex flex-row overflow-visible min-w-full " ref={containerRef}>
        {!screenShotMode && <style>{keyframes}</style>}
        {modifiedChildren}
        {copyModifiedChildren}
      </div>
      {indicator && indicator({ activeIndex, childCount: children?.length, activeIndicatorColor, inactiveIndicator })}
    </>
  );
}

export default InfiniteSlider;
